import {
  Bot,
  Check,
  CodeXml,
  Download,
  Layers,
  Map as MapIcon,
  SquareScissors,
} from "lucide-react";
import ClipLockup from "icons/clip-stacked.svg?react";
import VerifyLockup from "icons/verify-stacked.svg?react";
import type { ReactNode } from "react";
import type { Layout, Layouts } from "react-grid-layout";
import * as Routes from "routes";
import { CLIP_SHOWCASE } from "./cards/clip-plate";
import { CoreCard, type CoreFunction } from "./cards/core-card";
import { LinksCard } from "./cards/links-card";
import { QuickActionCard, type QuickAction } from "./cards/quick-action-card";
import { VERIFY_SHOWCASE } from "./cards/verify-plate";
import { VerifyWaitlistButton } from "./cards/verify-waitlist-dialog";
import { WhatsNewCard } from "./cards/whats-new-card";

/**
 * Grid breakpoints and column counts for react-grid-layout, the single source of
 * the dashboard's responsiveness.
 *
 * These are **container** widths: RGL's WidthProvider measures the grid element,
 * so they track the space the grid actually has, which shrinks when the sidebar
 * is open. They are not Tailwind's viewport breakpoints. Tune them here.
 */
export const GRID_BREAKPOINTS = { lg: 1024, md: 768, xs: 0 };
export const GRID_COLS = { lg: 12, md: 6, xs: 1 };
export const GRID_ROW_HEIGHT = 150;
export const GRID_MARGIN: [number, number] = [16, 16];

export type Breakpoint = keyof typeof GRID_COLS;
export const BREAKPOINTS: Breakpoint[] = ["lg", "md", "xs"];

/** Per-breakpoint size + resize bounds (in grid units) for a block. */
type BlockSizes = Record<
  Breakpoint,
  { w: number; h: number; minW: number; minH: number }
>;

export interface HomeBlockDef {
  id: string;
  title: string;
  description: string;
  sizes: BlockSizes;
  render: (editing: boolean) => ReactNode;
}

/** Uniform quick-action cards: quarter-width on desktop, two-up on tablet, stacked on mobile. */
const QUICK_ACTION_SIZES: BlockSizes = {
  lg: { w: 3, h: 1, minW: 3, minH: 1 },
  md: { w: 3, h: 1, minW: 3, minH: 1 },
  xs: { w: 1, h: 1, minW: 1, minH: 1 },
};

/** Core function cards. Resizable down to a bare link and up to the full width,
 *  since how much they explain is a function of how much room they are given. */
const CORE_SIZES: BlockSizes = {
  lg: { w: 8, h: 2, minW: 2, minH: 1 },
  md: { w: 6, h: 2, minW: 2, minH: 1 },
  xs: { w: 1, h: 2, minW: 1, minH: 1 },
};

/** The reference rail. A header plus five rows needs two rows of grid height. */
const LINKS_SIZES: BlockSizes = {
  lg: { w: 4, h: 2, minW: 2, minH: 2 },
  md: { w: 6, h: 2, minW: 2, minH: 2 },
  xs: { w: 1, h: 2, minW: 1, minH: 2 },
};

/** The What's New feed: full-width, its four article rows dividing the cell's
 *  height. The rows adapt to any cell the user drags out without scrolling, so
 *  minH 3 is only there to keep them tall enough to read. */
const WHATS_NEW_SIZES: BlockSizes = {
  lg: { w: 4, h: 3, minW: 3, minH: 3 },
  md: { w: 6, h: 3, minW: 3, minH: 3 },
  xs: { w: 1, h: 3, minW: 1, minH: 3 },
};

/**
 * Hub's four core functions. Each card carries three layers of content and its
 * rendered size decides how many are shown; see `.home-core` in index.css.
 *
 * Verify and Build have no landing page of their own, so each points at the
 * surface a new user is most likely to want first.
 */
const coreFunctions: CoreFunction[] = [
  {
    id: "clip",
    title: "Geoscape Clip",
    what: "See Geoscape data on a map and cut out the area you need.",
    why: "Browse every dataset visually, draw or pick a boundary, and take just that extract. No national download to wrangle and no GIS licence to buy first.",
    entries: [
      { label: "Open explorer", href: Routes.dataExplorer },
      { label: "New clip", href: Routes.clipUi },
      { label: "Downloads", href: Routes.dataDownload },
    ],
    icon: SquareScissors,
    color: "bg-chart-2",
    // Clip's brand teal, so its lockup and panel match its own tile rather than
    // coming out in Hub's blue primary.
    tint: "[--primary:var(--color-chart-2)]",
    lockup: ClipLockup,
    showcase: CLIP_SHOWCASE,
  },
  {
    id: "verify",
    title: "Geoscape Verify",
    what: "Every contact point, verified at the source.",
    why: "Geoscape builds G-NAF, the national address file. Verify checks addresses, phone, email and mail against it and its certified counterparts, and hands back an identifier you can store.",
    entries: [],
    icon: Check,
    color: "bg-primary",
    lockup: VerifyLockup,
    showcase: VERIFY_SHOWCASE,
    soon: true,
    waitlistCta: VerifyWaitlistButton,
  },
  {
    id: "data-catalogue",
    title: "Data Catalogue",
    what: "Every dataset we build, what is in it and how it is licensed.",
    why: "Start here when you are working out which product answers your question. Each entry lists coverage, how often it updates and the attributes it carries.",
    entries: [
      { label: "Browse datasets", href: Routes.dataCatalogue },
      { label: "Downloads", href: Routes.dataDownload },
      { label: "Release notes", href: Routes.docsPage },
    ],
    icon: Layers,
    color: "bg-chart-5",
  },
  {
    id: "build",
    title: "Geoscape Build",
    what: "Spatial APIs for your own application.",
    why: "Addresses, buildings, cadastre and basemaps behind one key. Wire them into your product instead of running pipelines to keep the data current.",
    entries: [
      { label: "API keys", href: Routes.developerApiKeys },
      { label: "API docs", href: Routes.docsApi },
      { label: "Usage", href: Routes.developerUsage },
    ],
    icon: CodeXml,
    color: "bg-chart-3",
    soon: true,
  },
];

/**
 * Entry points reached from a core card's links, or from the sidebar in Batch's
 * case. They are off the default page but stay in the registry so anyone who
 * wants a one-click tile for them can add it back from the Add block palette.
 * home-default-layout.ts hides them.
 */
const absorbedQuickActions: QuickAction[] = [
  {
    id: "explore",
    title: "Explore",
    description:
      "Browse and visualise Geoscape datasets in our interactive data explorer",
    href: Routes.dataExplorer,
    icon: MapIcon,
    color: "bg-chart-4",
  },
  {
    id: "download",
    title: "Download",
    description: "Download your licensed datasets and data products",
    href: Routes.dataDownload,
    icon: Download,
    color: "bg-chart-7",
  },
  {
    id: "batch",
    title: "Batch",
    description: "Perform batch geocoding and validation",
    href: Routes.batchBatches,
    icon: Bot,
    color: "bg-chart-8",
  },
  {
    id: "apps-and-apis",
    title: "Apps & APIs",
    description:
      "Integrate geocoding, validation, and more into your applications",
    href: Routes.developerApiKeys,
    icon: CodeXml,
    color: "bg-chart-3",
  },
];

export const HOME_BLOCKS: HomeBlockDef[] = [
  ...coreFunctions.map<HomeBlockDef>((fn) => ({
    id: fn.id,
    title: fn.title,
    description: fn.what,
    sizes: CORE_SIZES,
    render: (editing) => <CoreCard fn={fn} editing={editing} />,
  })),
  {
    id: "links",
    title: "Also here",
    description: "Documentation, subscription, demos, usage and what's new",
    sizes: LINKS_SIZES,
    render: (editing) => <LinksCard editing={editing} />,
  },
  {
    id: "whats-new",
    title: "What's New",
    description: "Latest updates, releases, and announcements",
    sizes: WHATS_NEW_SIZES,
    render: () => <WhatsNewCard />,
  },
  ...absorbedQuickActions.map<HomeBlockDef>((action) => ({
    id: action.id,
    title: action.title,
    description: action.description,
    sizes: QUICK_ACTION_SIZES,
    render: (editing) => <QuickActionCard action={action} editing={editing} />,
  })),
];

export const HOME_BLOCK_MAP: Record<string, HomeBlockDef> = Object.fromEntries(
  HOME_BLOCKS.map((b) => [b.id, b]),
);

// ---------------------------------------------------------------------------
// Layout model. The persisted shape is a set of react-grid-layout layouts (one
// per breakpoint) plus the ids the user has removed from their dashboard.
// Everything below is pure — no React.
// ---------------------------------------------------------------------------

export interface StoredHomeLayout {
  version: 3;
  layouts: Layouts;
  /** Block ids the user removed; excluded from every breakpoint's layout. */
  hidden: string[];
}

/** A block's layout item at a given breakpoint, in the first column of row `y`. */
function sizedItem(def: HomeBlockDef, bp: Breakpoint, y: number): Layout {
  const s = def.sizes[bp];
  return { i: def.id, x: 0, y, w: s.w, h: s.h, minW: s.minW, minH: s.minH };
}

/** The first free row below every item in a layout. */
function bottomRow(items: Layout[]): number {
  return items.reduce((row, l) => Math.max(row, l.y + l.h), 0);
}

/** Ids of blocks currently on the dashboard (known + not hidden), in registry order. */
export function visibleIds(stored: StoredHomeLayout): string[] {
  const hidden = new Set(stored.hidden);
  return HOME_BLOCKS.map((b) => b.id).filter((id) => !hidden.has(id));
}

/** Each breakpoint's layout limited to the currently-visible blocks. */
export function visibleLayouts(stored: StoredHomeLayout): Layouts {
  const visible = new Set(visibleIds(stored));
  const out: Layouts = {};
  for (const bp of BREAKPOINTS) {
    out[bp] = (stored.layouts[bp] ?? []).filter((l) => visible.has(l.i));
  }
  return out;
}

/** Remove a block from the dashboard — it moves to the Add block palette. */
export function removeBlock(
  stored: StoredHomeLayout,
  id: string,
): StoredHomeLayout {
  if (stored.hidden.includes(id)) return stored;
  return { ...stored, hidden: [...stored.hidden, id] };
}

/**
 * Re-add a hidden block, dropped at the bottom of every breakpoint's layout.
 *
 * RGL's own idiom for "put it last" is `y: Infinity`, but it only normalises the
 * breakpoint currently on screen, so the value survives in the other two and
 * `JSON.stringify` persists it as `null`. The bottom row is computed here instead.
 * Any gap left by hidden blocks still in the layout closes when RGL compacts.
 */
export function addBlock(
  stored: StoredHomeLayout,
  id: string,
): StoredHomeLayout {
  const def = HOME_BLOCK_MAP[id];
  if (!def) return stored;
  const layouts: Layouts = {};
  for (const bp of BREAKPOINTS) {
    const others = (stored.layouts[bp] ?? []).filter((l) => l.i !== id);
    layouts[bp] = [...others, sizedItem(def, bp, bottomRow(others))];
  }
  return {
    version: 3,
    layouts,
    hidden: stored.hidden.filter((h) => h !== id),
  };
}

/** A layout item RGL can position: every coordinate a finite number. */
function isSound(l: Layout): boolean {
  return [l.x, l.y, l.w, l.h].every(
    (n) => typeof n === "number" && Number.isFinite(n),
  );
}

/**
 * Repair a layout read back from storage. RGL rejects a non-finite coordinate
 * outright in development and silently mis-sorts the card in production, so an
 * unsound item is re-created at its registry size and stacked below the rest
 * rather than trusted or dropped. Items for ids no longer in the registry go.
 */
export function sanitizeLayouts(layouts: Layouts): Layouts {
  const out: Layouts = {};
  for (const bp of BREAKPOINTS) {
    const persisted = layouts[bp];
    const items = (Array.isArray(persisted) ? persisted : []).filter(
      (l: Layout | null) => !!l && HOME_BLOCK_MAP[l.i] !== undefined,
    );
    const repaired = items.filter(isSound);
    let y = bottomRow(repaired);
    for (const broken of items.filter((l) => !isSound(l))) {
      const def = HOME_BLOCK_MAP[broken.i];
      repaired.push(sizedItem(def, bp, y));
      y += def.sizes[bp].h;
    }
    out[bp] = repaired;
  }
  return out;
}
